import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { testimonials } from '@/mocks/testimonials';

export default function Testimonials() {
  const { t } = useTranslation();
  const [activeIndex, setActiveIndex] = useState(0);

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }, (_, i) => (
      <div key={i} className="w-4 h-4 flex items-center justify-center">
        {i < rating ? (
          <i className="ri-star-fill text-accent-500 text-sm" />
        ) : (
          <i className="ri-star-line text-accent-500 text-sm" />
        )}
      </div>
    ));
  };

  return (
    <section id="depoimentos" className="py-16 md:py-20 lg:py-24 bg-primary-950">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block text-accent-400 font-semibold text-sm uppercase tracking-wider mb-3">
              Depoimentos
            </span>
            <h2 className="font-heading font-bold text-2xl md:text-3xl lg:text-4xl text-background-50 mb-4">
              {t('testimonials.title')}
            </h2>
            <p className="text-background-300 text-base md:text-lg max-w-2xl mx-auto">
              {t('testimonials.subtitle')}
            </p>
          </div>

          {/* Desktop grid */}
          <div className="hidden md:grid grid-cols-2 lg:grid-cols-4 gap-5">
            {testimonials.map((item) => (
              <div
                key={item.id}
                className="bg-primary-900/60 border border-primary-700/40 rounded-lg p-5 flex flex-col"
              >
                {/* Stars */}
                <div className="flex items-center gap-0.5 mb-4">
                  {renderStars(item.rating)}
                </div>

                {/* Quote */}
                <p className="text-background-200 text-sm leading-relaxed flex-1 mb-5">
                  &ldquo;{item.text}&rdquo;
                </p>

                {/* Author */}
                <div className="flex items-center gap-3 pt-4 border-t border-primary-700/30">
                  <img
                    src={item.avatar}
                    alt={item.name}
                    className="w-11 h-11 rounded-full object-cover"
                  />
                  <div className="min-w-0">
                    <p className="font-heading font-semibold text-sm text-background-50 truncate">
                      {item.name}
                    </p>
                    <p className="text-xs text-background-400 truncate">
                      {item.role} · {item.company}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Mobile carousel */}
          <div className="md:hidden">
            <div className="bg-primary-900/60 border border-primary-700/40 rounded-lg p-5 mb-4">
              <div className="flex items-center gap-0.5 mb-4">
                {renderStars(testimonials[activeIndex].rating)}
              </div>
              <p className="text-background-200 text-sm leading-relaxed mb-5">
                &ldquo;{testimonials[activeIndex].text}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-4 border-t border-primary-700/30">
                <img
                  src={testimonials[activeIndex].avatar}
                  alt={testimonials[activeIndex].name}
                  className="w-11 h-11 rounded-full object-cover"
                />
                <div className="min-w-0">
                  <p className="font-heading font-semibold text-sm text-background-50 truncate">
                    {testimonials[activeIndex].name}
                  </p>
                  <p className="text-xs text-background-400 truncate">
                    {testimonials[activeIndex].role} · {testimonials[activeIndex].company}
                  </p>
                </div>
              </div>
            </div>

            {/* Dots */}
            <div className="flex items-center justify-center gap-2">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveIndex(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-colors cursor-pointer ${
                    i === activeIndex ? 'bg-accent-500' : 'bg-primary-700'
                  }`}
                  type="button"
                  aria-label={`Depoimento ${i + 1}`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}